import {
  Link,
  ListItem,
  ListItemText,
  Slide,
  Typography,
} from '@material-ui/core';
import { Check, Info, Lock } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import { useFormikEnhanced } from '@superdispatch/forms';
import {
  ColorDynamic,
  Column,
  Columns,
  DrawerActions,
  Inline,
  Stack,
  useSnackbarStack,
} from '@superdispatch/ui';
import { Alert, Box, Button, TextBox } from '@superdispatch/ui-lab';
import { APIError, isAPIError } from 'shared/api/APIError';
import { useCreditCard } from 'shared/errors/PaymentService';
import { defaultFormErrorsGetter } from 'shared/helpers/FormHelpers';
import { ChargebeeEmptyPlaceholder } from 'shared/ui/ChargebeeEmptyPlaceholder';
import {
  useBillingAddress,
  useSubscription,
  useSubscriptionAPI,
} from '../SubscriptionAPI';
import { trackSubscriptionEvent } from '../data/SubscriptionAnalytics';
import { SubscriptionChargebeeCard } from './SubscriptionChargebeeCard';
import { BackButton } from 'shared/ui/BackButton';
import { FormikDrawerContent } from 'shared/form/FormikDrawer';
import { SubscriptionBillingForm } from './SubscriptionBillingForm';
import { useCarrierInfo } from 'shared/modules/carrier-profile/data/CarrierInfoAPI';
import { useShowAddBillingAddressDialog } from './useShowAddBillingAddressDialog';
import { useFlag } from 'shared/settings/FeatureToggles';
import { toFormikValidationSchema } from 'shared/form/FormSchema';
import { subscriptionContactSchema } from '../SubscriptionDTO';
import { SubscriptionContactFields } from './SubscriptionContactFields';

const contactValidationSchema = toFormikValidationSchema(
  subscriptionContactSchema,
);

export interface CardDetailsDrawerContentProps {
  onClose: () => void;
  onBack: () => void;
}

export function CardDetailsDrawerContent({
  onClose,
  onBack,
}: CardDetailsDrawerContentProps) {
  const { subscription, isLoading } = useSubscription();
  const { data: billingAddress } = useBillingAddress();
  const { doesBillingAddressNotExist } = useShowAddBillingAddressDialog();
  const {
    saveBillingAddress,
    updateSubscriptionContact,
    updateSubscriptionSettings,
  } = useSubscriptionAPI();
  const { addCreditCard, cardRef, isChargebeeAvailable } = useCreditCard(
    subscription?.client_token || '',
  );
  const { data: carrierInfo } = useCarrierInfo({
    enabled: doesBillingAddressNotExist,
  });

  const { addSnackbar } = useSnackbarStack();
  const isSubscriptionContactEnabled = useFlag('ctms_subscription_contact');
  const subscriptionContact = subscription?.subscription?.contact;

  const formik = useFormikEnhanced({
    validateOnBlur: false,
    validationSchema: isSubscriptionContactEnabled
      ? contactValidationSchema
      : undefined,
    initialValues: {
      cardholder_name: '',
      zip: billingAddress?.billing_zip || carrierInfo?.zip || '',
      country: billingAddress?.billing_country || carrierInfo?.country || 'US',
      state: billingAddress?.billing_state || carrierInfo?.state || '',
      city: billingAddress?.billing_city || carrierInfo?.city || '',
      address: billingAddress?.billing_address || carrierInfo?.address || '',
      contact_first_name: subscriptionContact?.first_name || '',
      contact_last_name: subscriptionContact?.last_name || '',
      contact_email: subscriptionContact?.email || '',
    },
    getFormErrors: (error) => {
      return defaultFormErrorsGetter(error);
    },
    onSubmit: async (values) => {
      const [firstName, lastName] = values.cardholder_name.split(' ');

      try {
        await saveBillingAddress({
          billing_country: values.country,
          billing_zip: values.zip,
          billing_state: values.state,
          billing_city: values.city,
          billing_address: values.address,
        });

        // Compare with the saved contact, so a retry after a declined card
        // does not send the already saved contact again.
        const isContactChanged =
          values.contact_first_name.trim() !==
            subscriptionContact?.first_name ||
          values.contact_last_name.trim() !== subscriptionContact?.last_name ||
          values.contact_email.trim() !== subscriptionContact?.email;
        if (isSubscriptionContactEnabled && isContactChanged) {
          await updateSubscriptionContact({
            first_name: values.contact_first_name.trim(),
            last_name: values.contact_last_name.trim(),
            email: values.contact_email.trim(),
          });
        }
        const { token, vaultToken } = await addCreditCard({
          firstName,
          lastName,
        });
        return await updateSubscriptionSettings({
          payment_method_nonce: token,
          id: token,
          id_at_vault: vaultToken,
        });
      } catch (err: unknown) {
        if (isAPIError(err)) {
          const errorDetails = (err as APIError<{ details: string[] }>).context
            ?.details;

          if (Array.isArray(errorDetails)) {
            err.message = errorDetails.join('\n');
          }
        }

        return Promise.reject(err);
      }
    },
    onSubmitSuccess: () => {
      onBack();
      trackSubscriptionEvent({
        name: 'Carrier Edited Subscription Payment Information',
      });
      addSnackbar('Payment information was successfully updated', {
        variant: 'success',
      });
    },
  });

  return (
    <FormikDrawerContent
      formik={formik}
      onClose={onClose}
      title={
        <Inline>
          <BackButton onClick={onBack} />
          <Typography variant="h2">Edit Card Details</Typography>
        </Inline>
      }
      actions={
        !!subscription && (
          <Stack space="small">
            <DrawerActions>
              <Columns align="center">
                <Column width="1/2">
                  <Button
                    startIcon={<Lock />}
                    type="submit"
                    pending={formik.isSubmitting}
                  >
                    Save
                  </Button>
                </Column>

                <Column width="1/2">
                  <Inline horizontalAlign="right">
                    <Link
                      target="_blank"
                      rel="noopener noreferrer"
                      href="https://support.superdispatch.com/hc/articles/35648690974739"
                    >
                      Refund Policy
                    </Link>
                  </Inline>
                </Column>
              </Columns>
            </DrawerActions>

            {formik.status.type === 'rejected' && (
              <Box maxWidth="500px" paddingRight="large" paddingLeft="large">
                <Alert
                  icon={
                    <Info fontSize="small" htmlColor={ColorDynamic.Red500} />
                  }
                  severity="critical"
                >
                  {formik.status.payload.message}
                </Alert>
              </Box>
            )}
          </Stack>
        )
      }
    >
      <Slide
        in={true}
        timeout={200}
        direction="up"
        mountOnEnter={true}
        unmountOnExit={false}
      >
        <Stack space="small">
          <Box maxWidth="440px">
            <Stack space="xsmall">
              <Columns space="xxsmall" align="top">
                <Column width="content">
                  <Lock fontSize="small" htmlColor={ColorDynamic.Dark100} />
                </Column>
                <Column width="fluid">
                  <TextBox color="secondary" variant="body">
                    We use industry-standard encryption and security protocols
                    to safeguard your information. Your card details are not
                    stored on our internal servers for your protection.
                  </TextBox>
                </Column>
              </Columns>
              <Columns space="xxsmall" align="top">
                <Column width="content">
                  <Check fontSize="small" htmlColor={ColorDynamic.Dark100} />
                </Column>
                <Column width="fluid">
                  <TextBox color="secondary" variant="body">
                    We accept all major credit cards
                  </TextBox>
                </Column>
              </Columns>
            </Stack>
          </Box>

          {isLoading ? (
            <Stack space="small">
              <Skeleton />
              <Skeleton />
            </Stack>
          ) : !subscription ? (
            <ListItem>
              <ListItemText
                disableTypography={true}
                primary={
                  <Typography color="textSecondary" align="center" variant="h4">
                    There are no subscription details
                  </Typography>
                }
              />
            </ListItem>
          ) : isChargebeeAvailable ? (
            <SubscriptionChargebeeCard
              hasCardHolderName={false}
              ref={cardRef}
            />
          ) : (
            <ChargebeeEmptyPlaceholder />
          )}
          {isSubscriptionContactEnabled ? (
            <>
              <Stack space="xxsmall">
                <Typography variant="h4">Subscription Contact</Typography>
                <SubscriptionContactFields />
              </Stack>
              <Stack space="small">
                <Typography variant="h4">Billing Address</Typography>
                <SubscriptionBillingForm />
              </Stack>
            </>
          ) : (
            <SubscriptionBillingForm />
          )}
        </Stack>
      </Slide>
    </FormikDrawerContent>
  );
}
